import { useMemo } from "react";
import { useRouter } from "next/router";

import { generateUrlArguments, timezone } from "@/utils";

import useForm from "@/hooks/useHookForm";
import useFormPayment from "@/hooks/useFormPayment";

import { useAppSelector } from "@/hooks/useRedux";

interface useBookTrialFormProps {
  packData?: any;
  setFormError?: (error: string) => void;
  slot: any;
}

const useBookTrialForm = ({
  packData,
  setFormError = () => {},
  slot,
}: useBookTrialFormProps) => {
  const router = useRouter();

  const { countryCodeData } = useAppSelector((state) => state.dashboard);
  const { slotsData } = useAppSelector((state) => state.bootCamp);

  let countryCodeDefault = useMemo(
    () => countryCodeData?.default?.code || "+91",
    [countryCodeData]
  );

  const { selectedOneOnOneYogaDate, selectedOneOnOneYogaSlot } = slot || {};

  const defaultValues = useMemo(
    () => ({
      name: "",
      email: "",
      mobileCountryCode: countryCodeDefault,
      mobile: "",
      nutritionGoalID: "",
      selectedOneOnOneYogaDate,
      selectedOneOnOneYogaSlot,
    }),
    [countryCodeDefault, selectedOneOnOneYogaDate, selectedOneOnOneYogaSlot]
  );

  const urlsArguments = generateUrlArguments("lead_via-mobile_app=true");

  const { methods, resetData } = useForm({
    defaultValues,
  });

  const { getValues } = methods;

  const onSuccessCallback = ({ amount_paid, currency }) => {
    const { selectedOneOnOneYogaDate } = getValues();

    const dateObj = slotsData?.find(
      ({ date }) => date === selectedOneOnOneYogaDate
    );

    const date = `${dateObj?.display_date} ${
      selectedOneOnOneYogaDate?.split("-")?.[0]
    }`;

    resetData();

    const isPSTimezone = [
      "US/Pacific",
      "America/Vancouver",
      "America/Los_Angeles",
    ]?.includes(timezone);

    router.push({
      pathname: `/plans/payments/yoga/1-on-1-yoga/success/`,
      query: {
        ...router?.query,
        date,
        time: `${selectedOneOnOneYogaSlot} ${isPSTimezone ? "(PST)" : "(EST)"}`,
        amount_paid,
        currency
      },
    });
  };

  const onFailureCallback = () => {
    resetData();
    router.push({
      pathname: "/payments/failure/",
      query: router?.query,
    });
  };

  const { isLoading, onBlur, onPackPurchase } = useFormPayment({
    methods,
    packData,
    urlsArguments,
    timezone,
    onSuccessCallback,
    onFailureCallback,
  });

  const { handleSubmit } = methods;

  const packPurchaseHandler = handleSubmit((formValues) => {
    setFormError("");

    const { selectedOneOnOneYogaDate, selectedOneOnOneYogaSlot } = formValues;
    const dateObj = slotsData?.find(
      ({ date }) => date === selectedOneOnOneYogaDate
    );
    const slotClassId = dateObj?.slots?.find(
      ({ start_time }) => start_time == selectedOneOnOneYogaSlot
    )

    const additionalData = { 
      is_trial_sales: true,
      customer_goals: "Online Yoga class",
      customer_symptoms: "Online Yoga class",
      datetime_str: `${formValues?.selectedOneOnOneYogaDate} ${formValues?.selectedOneOnOneYogaSlot}`,
      package_start_date: formValues?.selectedOneOnOneYogaDate,
      page_type: "yoga_pack_trial",
      goal: 1,
      trial_sales_form: null,
      yoga_class: slotClassId?.class_id,
      nutrition_goal_id: 1
    };

    const keys = ["customer_goals","customer_symptoms","datetime_str","package_start_date","page_type","goal","trial_sales_form","yoga_class"]

    return onPackPurchase({
      keys,
      ...formValues,
      ...additionalData
    });
  });

  return {
    methods,
    isLoading,
    onBlur,
    packPurchaseHandler,
  };
};

export default useBookTrialForm;
